import React, { useEffect, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { fetchSetupStatus, selectSetupStatus } from "../redux/branches.slice.ts"
import { BranchSetupWizard } from "./BranchSetupWizard.tsx"
import { ContinueBranchSetupBanner } from "./ContinueBranchSetupBanner.tsx"

function autoShowKey(gccId: string | null | undefined) {
  return `branch_setup_wizard_auto_shown_${gccId ?? "unknown"}`
}

/**
 * Home's entry point into branch setup: fetches the status every other
 * branch-setup surface already fetches on its own mount (matching this
 * repo's per-component-fetch convention), renders the CTA banner, and
 * auto-opens the wizard once per login session for an owner with
 * incomplete setup — tracked by a `sessionStorage` flag (cleared by a new
 * sign-in) rather than anything persisted server-side, so it nags once per
 * session rather than on every Home visit, while the banner itself always
 * stays available as a one-click way back in.
 */
export function BranchSetupFlow() {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectSetupStatus)
  const gccId = useAppSelector((state) => state.branches.gcc?.id)
  const [wizardOpen, setWizardOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchSetupStatus())
  }, [dispatch])

  useEffect(() => {
    if (!status || status.done || status.target <= 1) return
    const key = autoShowKey(gccId)
    try {
      if (window.sessionStorage.getItem(key)) return
      window.sessionStorage.setItem(key, "1")
    } catch {
      // Storage unavailable (private mode, disabled) — fall back to the
      // banner-only experience rather than auto-opening on every mount.
      return
    }
    setWizardOpen(true)
  }, [status, gccId])

  return (
    <>
      <ContinueBranchSetupBanner onContinueSetup={() => setWizardOpen(true)} />
      <BranchSetupWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  )
}
